import { useState, useEffect } from 'react';
import { ArrowLeft } from 'lucide-react';
import { api, ContactTelegramPrivacy, CandidateProfile } from '../../lib/api';

interface PrivacySettingsViewProps {
  onClose: () => void;
}

export function PrivacySettingsView({ onClose }: PrivacySettingsViewProps) {
  const [privacy, setPrivacy] = useState<ContactTelegramPrivacy>('no');
  const [loadingPrivacy, setLoadingPrivacy] = useState(false);
  const [savingPrivacy, setSavingPrivacy] = useState(false);
  const [allowChatMessages, setAllowChatMessages] = useState<boolean>(true);
  const [savingChatPrivacy, setSavingChatPrivacy] = useState(false);
  const [blockedUsers, setBlockedUsers] = useState<CandidateProfile[]>([]);
  const [loadingBlocks, setLoadingBlocks] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const fetchSettings = async () => {
      try {
        setLoadingPrivacy(true);
        setLoadingBlocks(true);

        const [profile, blocks] = await Promise.all([
          api.profile.get(),
          api.user.getBlocks(),
        ]);

        if (cancelled) return;

        const profilePrivacy = (profile.contact_telegram_privacy as ContactTelegramPrivacy) || 'no';
        setPrivacy(profilePrivacy);
        setAllowChatMessages(profile.allow_chat_messages ?? true);
        setBlockedUsers(blocks.data || []);
      } catch (error) {
        console.error('Failed to load privacy settings:', error);
      } finally {
        if (!cancelled) {
          setLoadingPrivacy(false);
          setLoadingBlocks(false);
        }
      }
    };

    fetchSettings();

    return () => {
      cancelled = true;
    };
  }, []);

  const handleChangePrivacy = async (value: ContactTelegramPrivacy) => {
    if (value === privacy || savingPrivacy) return;
    try {
      setSavingPrivacy(true);
      await api.profile.update({ contact_telegram_privacy: value });
      setPrivacy(value);
    } catch (error) {
      console.error('Failed to update Telegram username privacy:', error);
    } finally {
      setSavingPrivacy(false);
    }
  };

  const handleUnblock = async (userId: number) => {
    try {
      await api.user.unblock(userId);
      setBlockedUsers((prev) => prev.filter((u) => u.telegram_id !== userId));
    } catch (error) {
      console.error('Failed to unblock user:', error);
    }
  };

  const handleToggleChatPrivacy = async (mode: 'auto' | 'manual') => {
    if (savingChatPrivacy) return;
    const shouldAllow = mode === 'auto';
    if (shouldAllow === allowChatMessages) return;

    try {
      setSavingChatPrivacy(true);
      await api.profile.update({ allow_chat_messages: shouldAllow });
      setAllowChatMessages(shouldAllow);
    } catch (error) {
      console.error('Failed to update chat privacy setting:', error);
    } finally {
      setSavingChatPrivacy(false);
    }
  };

  const renderPrivacyOption = (
    value: ContactTelegramPrivacy,
    title: string,
    description: string,
  ) => {
    const isActive = privacy === value;
    return (
      <button
        key={value}
        onClick={() => handleChangePrivacy(value)}
        className="w-full p-3 rounded-xl border flex flex-col items-start text-left transition-all hover:border-[#00FF88]/40"
        style={{
          background: isActive ? 'rgba(0, 255, 136, 0.08)' : 'rgba(255, 255, 255, 0.02)',
          borderColor: isActive ? 'rgba(0, 255, 136, 0.6)' : 'rgba(255, 255, 255, 0.12)',
        }}
      >
        <div className="flex items-center justify-between w-full">
          <span style={{ color: '#FFFFFF', fontSize: '14px' }}>{title}</span>
          <span
            className="w-4 h-4 rounded-full border flex items-center justify-center"
            style={{
              borderColor: isActive ? '#00FF88' : 'rgba(255, 255, 255, 0.3)',
            }}
          >
            {isActive && (
              <span
                className="w-2 h-2 rounded-full"
                style={{ background: '#00FF88' }}
              />
            )}
          </span>
        </div>
        <span
          style={{
            color: 'rgba(255, 255, 255, 0.6)',
            fontSize: '12px',
            marginTop: '4px',
          }}
        >
          {description}
        </span>
      </button>
    );
  };

  return (
    <div className="min-h-screen bg-splyt-gradient flex flex-col">
      {/* Header */}
      <div className="px-4 pt-4 pb-2 flex items-center gap-4 border-b border-white/10 bg-black/20 backdrop-blur-xl sticky top-0 z-10">
        <button
          onClick={onClose}
          className="w-10 h-10 rounded-full border backdrop-blur-xl flex items-center justify-center transition-all hover:border-white/30"
          style={{
            background: 'rgba(255, 255, 255, 0.05)',
            borderColor: 'rgba(255, 255, 255, 0.12)',
          }}
        >
          <ArrowLeft className="w-5 h-5" style={{ color: 'rgba(255, 255, 255, 0.7)' }} />
        </button>
        <h1 className="text-xl font-bold text-white">Приватність</h1>
      </div>

      <div className="p-4 space-y-6 flex-1 overflow-y-auto">
        {/* Privacy Settings */}
        <div>
          <p
            style={{
              color: 'rgba(255, 255, 255, 0.5)',
              fontSize: '12px',
              marginBottom: '8px',
            }}
          >
            ПРИВАТНІСТЬ TELEGRAM
          </p>
          <div
            className="p-3 rounded-xl border space-y-2"
            style={{
              background: 'rgba(255, 255, 255, 0.03)',
              borderColor: 'rgba(255, 255, 255, 0.08)',
            }}
          >
            {loadingPrivacy ? (
              <p style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '13px' }}>
                Завантаження налаштувань...
              </p>
            ) : (
              <>
                {renderPrivacyOption(
                  'no',
                  'Не показувати username',
                  'Мій Telegram username не буде показаний іншим користувачам.',
                )}
                {renderPrivacyOption(
                  'ask',
                  'Питати в чаті',
                  'Спочатку познайомимося у внутрішньому чаті, а потім я вирішу, чи ділитися username.',
                )}
                {renderPrivacyOption(
                  'liked_only',
                  'Тільки для профілів, які я лайкнув',
                  'Username бачать лише профілі, яким я поставив лайк / з якими є матч.',
                )}
                {renderPrivacyOption(
                  'everyone',
                  'Так, для всіх',
                  'Мій Telegram username можуть бачити всі користувачі.',
                )}
                {savingPrivacy && (
                  <p
                    style={{
                      color: 'rgba(255, 255, 255, 0.5)',
                      fontSize: '12px',
                      marginTop: '4px',
                    }}
                  >
                    Збереження...
                  </p>
                )}
              </>
            )}
          </div>
        </div>

        {/* In-app chat privacy */}
        <div>
          <p
            style={{
              color: 'rgba(255, 255, 255, 0.5)',
              fontSize: '12px',
              marginBottom: '8px',
            }}
          >
            ПОВІДОМЛЕННЯ В ДОДАТКУ
          </p>
          <div
            className="p-3 rounded-xl border space-y-2"
            style={{
              background: 'rgba(255, 255, 255, 0.03)',
              borderColor: 'rgba(255, 255, 255, 0.08)',
            }}
          >
            <button
              onClick={() => handleToggleChatPrivacy('auto')}
              className="w-full p-3 rounded-xl border flex flex-col items-start text-left transition-all hover:border-[#00FF88]/40"
              style={{
                background: allowChatMessages ? 'rgba(0, 255, 136, 0.08)' : 'rgba(255, 255, 255, 0.02)',
                borderColor: allowChatMessages
                  ? 'rgba(0, 255, 136, 0.6)'
                  : 'rgba(255, 255, 255, 0.12)',
              }}
            >
              <div className="flex items-center justify-between w-full">
                <span style={{ color: '#FFFFFF', fontSize: '14px' }}>
                  Автоматичний прийом
                </span>
                <span
                  className="w-4 h-4 rounded-full border flex items-center justify-center"
                  style={{
                    borderColor: allowChatMessages
                      ? '#00FF88'
                      : 'rgba(255, 255, 255, 0.3)',
                  }}
                >
                  {allowChatMessages && (
                    <span
                      className="w-2 h-2 rounded-full"
                      style={{ background: '#00FF88' }}
                    />
                  )}
                </span>
              </div>
              <span
                style={{
                  color: 'rgba(255, 255, 255, 0.6)',
                  fontSize: '12px',
                  marginTop: '4px',
                }}
              >
                Інші користувачі з лайком/матчем можуть писати вам у чат без додаткового
                запиту.
              </span>
            </button>

            <button
              onClick={() => handleToggleChatPrivacy('manual')}
              className="w-full p-3 rounded-xl border flex flex-col items-start text-left transition-all hover:border-[#00FF88]/40"
              style={{
                background: !allowChatMessages ? 'rgba(0, 255, 136, 0.08)' : 'rgba(255, 255, 255, 0.02)',
                borderColor: !allowChatMessages
                  ? 'rgba(0, 255, 136, 0.6)'
                  : 'rgba(255, 255, 255, 0.12)',
              }}
            >
              <div className="flex items-center justify-between w-full">
                <span style={{ color: '#FFFFFF', fontSize: '14px' }}>
                  Ручне схвалення
                </span>
                <span
                  className="w-4 h-4 rounded-full border flex items-center justify-center"
                  style={{
                    borderColor: !allowChatMessages
                      ? '#00FF88'
                      : 'rgba(255, 255, 255, 0.3)',
                  }}
                >
                  {!allowChatMessages && (
                    <span
                      className="w-2 h-2 rounded-full"
                      style={{ background: '#00FF88' }}
                    />
                  )}
                </span>
              </div>
              <span
                style={{
                  color: 'rgba(255, 255, 255, 0.6)',
                  fontSize: '12px',
                  marginTop: '4px',
                }}
              >
                Нові чати не можуть писати вам напряму. Ви самі ініціюєте розмову або
                включите автоматичний прийом.
              </span>
            </button>

            {savingChatPrivacy && (
              <p
                style={{
                  color: 'rgba(255, 255, 255, 0.5)',
                  fontSize: '12px',
                  marginTop: '4px',
                }}
              >
                Збереження...
              </p>
            )}
          </div>
        </div>

        {/* Blocked Users */}
        <div>
          <p
            style={{
              color: 'rgba(255, 255, 255, 0.5)',
              fontSize: '12px',
              marginBottom: '8px',
            }}
          >
            ЗАБЛОКОВАНІ КОРИСТУВАЧІ
          </p>
          <div
            className="p-3 rounded-xl border space-y-2"
            style={{
              background: 'rgba(255, 255, 255, 0.03)',
              borderColor: 'rgba(255, 255, 255, 0.08)',
            }}
          >
            {loadingBlocks ? (
              <p style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '13px' }}>
                Завантаження списку...
              </p>
            ) : blockedUsers.length === 0 ? (
              <p style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '13px' }}>
                У вас немає заблокованих користувачів.
              </p>
            ) : (
              blockedUsers.map((user) => {
                const name =
                  user.name ||
                  (user.username ? `@${user.username}` : `ID ${user.telegram_id}`);
                return (
                  <div
                    key={user.telegram_id}
                    className="flex items-center justify-between gap-2 p-2 rounded-lg"
                    style={{
                      background: 'rgba(255, 255, 255, 0.02)',
                    }}
                  >
                    <span
                      className="truncate"
                      style={{ color: '#FFFFFF', fontSize: '13px', maxWidth: '65%' }}
                    >
                      {name}
                    </span>
                    <button
                      onClick={() => handleUnblock(user.telegram_id)}
                      className="px-3 py-1 rounded-full border text-xs transition-all hover:border-[#00FF88]/60"
                      style={{
                        borderColor: 'rgba(255, 255, 255, 0.3)',
                        color: 'rgba(255, 255, 255, 0.9)',
                      }}
                    >
                      Розблокувати
                    </button>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
